/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: property_images entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the property_images table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the property_images API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, property_id, data_source_id, file_name, storage_key, file_url, caption, display_order, is_primary, created_by_user_profile_id, created_at, updated_at, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id]
 * Searchable fields: []
 * Filterable fields: [id, property_id, data_source_id, created_by_user_profile_id, archived_by_user_profile_id, archive_batch_id]
 */

import { httpClient } from '../http-client.js';
import { type UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  PropertyImagesRow,
  PropertyImagesInsert,
  PropertyImagesUpdate
} from '../../types/generated_schemas/generated-property_images-schema.js';

export interface PropertyImagesQueryOptions extends UnifiedQueryOptions {}

export interface PropertyImagesResponse {
  data: PropertyImagesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for property_images table operations
 *
 * @description Provides type-safe access to property_images API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class PropertyImagesClient {
  private readonly basePath = '/property-images';

  /**
   * Get all property_images records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of property_images records
   */
  async getAll(options?: PropertyImagesQueryOptions): Promise<PropertyImagesRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sort', options.sortBy);
    if (options?.sortOrder) params.set('order', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<PropertyImagesResponse>(url);
    return response.data;
  }

  /**
   * Get a single property_images record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the property_images record
   */
  async getById(id: string): Promise<PropertyImagesRow> {
    const response = await httpClient.get<{
      data: PropertyImagesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new property_images record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created property_images record
   */
  async create(data: PropertyImagesInsert): Promise<PropertyImagesRow> {
    const response = await httpClient.post<{
      data: PropertyImagesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing property_images record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated property_images record
   */
  async update(id: string, data: PropertyImagesUpdate): Promise<PropertyImagesRow> {
    const response = await httpClient.put<{
      data: PropertyImagesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a property_images record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple property_images records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created property_images records
   */
  async createMany(data: PropertyImagesInsert[]): Promise<PropertyImagesRow[]> {
    const response = await httpClient.post<{
      data: PropertyImagesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Search property_images with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: PropertyImagesQueryOptions
  ): Promise<PropertyImagesResponse> {
    throw new Error('Search operations are not available for property_images - no searchable fields configured');
  }

  /**
   * Get count of property_images records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a property_images record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'property_images',
      publicFields: ['id', 'property_id', 'data_source_id', 'file_name', 'storage_key', 'file_url', 'caption', 'display_order', 'is_primary', 'created_by_user_profile_id', 'created_at', 'updated_at', 'archived_at', 'archived_by_user_profile_id', 'archive_reason', 'archive_batch_id'],
      searchableFields: [],
      filterableFields: ['id', 'property_id', 'data_source_id', 'created_by_user_profile_id', 'archived_by_user_profile_id', 'archive_batch_id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'updated_at'],
    };
  }
}

// Export singleton instance for convenience
export const propertyImagesClient = new PropertyImagesClient();
